import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Feather, HandHeart, Sparkles, ArrowRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import StarField from "@/components/StarField";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, ease: "easeOut" },
};

export default function Home() {
    return (
        <SiteLayout transparentNav>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <StarField density={120} className="absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 sm:pt-32 sm:pb-40 text-center">
                    <motion.div {...fadeUp}>
                        <div className="label mb-6" data-testid="hero-eyebrow">
                            ✦ Ein Ort für Stadttauben ✦
                        </div>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight mb-8"
                            data-testid="hero-title"
                        >
                            Erinnerung.
                            <br />
                            <span className="italic text-[#f0c674]">Hilfe.</span>
                            <br />
                            Zweite Chancen.
                        </h1>
                        <p className="text-lg sm:text-xl text-[#94a3b8] max-w-2xl mx-auto leading-relaxed mb-12">
                            Stadttauben sind unsichtbare Begleiter unserer Städte. Hier finden sie
                            einen leuchtenden Platz — in der Erinnerung, in der konkreten Hilfe und
                            in einem neuen Zuhause.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Link to="/taubenhimmel" className="btn-primary" data-testid="hero-cta-sky">
                                In den Taubenhimmel <ArrowRight size={16} />
                            </Link>
                            <Link to="/himmelsfonds" className="btn-secondary" data-testid="hero-cta-fund">
                                Hilfsfonds entdecken
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* THREE AREAS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    <AreaCard
                        to="/taubenhimmel"
                        icon={Sparkles}
                        eyebrow="01 / Erinnerung"
                        title="Digitaler Taubenhimmel"
                        text="Ein leuchtender Platz für jede Taube, die uns wichtig war. Geschichten, Kerzen, Federn — geteilt, geschützt, verbunden."
                        cta="Den Himmel betrachten"
                        testid="area-card-sky"
                    />
                    <AreaCard
                        to="/himmelsfonds"
                        icon={HandHeart}
                        eyebrow="02 / Hilfe"
                        title="Himmelsfond"
                        text="Pro Eintrag im Taubenhimmel fließt 1 € direkt in den Fonds. Plus jede Spende — zu 100 % zweckgebunden für lebende Tauben."
                        cta="Fall einreichen"
                        testid="area-card-fund"
                    />
                    <AreaCard
                        to="/vermittlung"
                        icon={Feather}
                        eyebrow="03 / Zukunft"
                        title="Vermittlung"
                        text="Handicap, Flugfähig, Notfall, Pflegestelle. Für jede Taube ein passender Mensch — und für jeden Menschen eine passende Taube."
                        cta="Tauben suchen"
                        testid="area-card-adoption"
                    />
                </div>
            </section>

            {/* MANIFEST */}
            <section className="border-t border-white/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
                    <div className="label mb-6">Unser Versprechen</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8">
                        Jede Taube zählt — auch wenn die Welt es nicht sehen will.
                    </h2>
                    <p className="text-[#94a3b8] text-lg leading-relaxed">
                        Wir glauben, dass keine Stadttaube vergessen werden sollte. Nicht im Tod,
                        nicht in der Not, nicht auf der Suche nach einem Zuhause. Taubenhimmel ist
                        ein leiser Ort, an dem all das einen Platz hat.
                    </p>
                </div>
            </section>
        </SiteLayout>
    );
}

function AreaCard({ to, icon: Icon, eyebrow, title, text, cta, testid }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
        >
            <Link
                to={to}
                className="glass gold-glow group block p-8 lg:p-10 h-full transition-all duration-500 hover:-translate-y-1"
                data-testid={testid}
            >
                <Icon size={28} className="text-[#f0c674] mb-6" strokeWidth={1.3} />
                <div className="label mb-3">{eyebrow}</div>
                <h3 className="font-display text-3xl mb-4 leading-tight">{title}</h3>
                <p className="text-[#94a3b8] leading-relaxed mb-8">{text}</p>
                <div className="inline-flex items-center gap-2 text-[#f0c674] text-sm font-medium group-hover:gap-3 transition-all">
                    {cta} <ArrowRight size={14} />
                </div>
            </Link>
        </motion.div>
    );
}
